import { getBodyPartNameById, LOCATION_ID_MAPPING } from '../../../utilities/bodySurvey.util'
import { getFormattedDate, getFormattedTime } from '../../../utilities/dateTime.util'
import {
  IBodySurveyCategory,
  IBodySurveyGroup,
  IBodySurveyPart,
  IChartSummaryEventEntry,
  IChartSummaryEventKeyValue,
  IChartSummaryPageListModel,
  IOptionGroup,
  IQaChartSummary,
  IQaChartSummaryElement,
  IQaChartSummaryPage,
  IQaChartSummarySection,
  IQaChartSummaryValueRecord,
} from '../qaAddendums/store/qaAddendum.types'
import { TIMELINE_SUMMARY_CONFIG } from './timelineSummary.config'

const OPTIONS_KEY = 'options'
const GROUP_ID_KEY_PATTERN = /id$/i
const GROUP_NAME_KEY_PATTERN = /name$|label|title/i

const BODY_SURVEY_PATH_SEGMENT = 'bodySurveys'
const BODY_SURVEY_CATEGORY_ORDER = ['Body Parts', 'General Extremities', 'External/Skin', 'Neuro', 'Mental', 'Heart']

const ROOT_PAGE_NAME = 'Root'

export type ChartSummarySection = 'dispatch' | 'patient'

const CHART_SUMMARY_PAGES: { name: string; section: ChartSummarySection; descriptionKey: string }[] = [
  {
    name: 'Shift Startup',
    section: 'dispatch',
    descriptionKey: 'qaFlags.qaAddendum.flagResolution.chartSummaryPageDescription.ShiftStartup',
  },
  { name: 'Trip Information', section: 'dispatch', descriptionKey: 'fullChart.dispatch.PriorityNatureAndDisposition' },
  {
    name: 'Pickup Address',
    section: 'dispatch',
    descriptionKey: 'fullChart.dispatch.LocationAndDetailsOfPatientScene',
  },
  {
    name: 'Scene Description',
    section: 'dispatch',
    descriptionKey: 'fullChart.dispatch.DetailsOfSceneAndOtherRespondingAgencies',
  },
  {
    name: 'Destination Address',
    section: 'dispatch',
    descriptionKey: 'fullChart.dispatch.LocationAndDetailsOfReceivingFacility',
  },
  {
    name: 'Factors Affecting Care',
    section: 'dispatch',
    descriptionKey: 'fullChart.dispatch.DispatchResponseAndOtherFactors',
  },
  { name: 'Exposure', section: 'dispatch', descriptionKey: 'fullChart.dispatch.ExposureDetailsProtectiveEquipment' },
  { name: 'NEMSIS Data', section: 'dispatch', descriptionKey: 'fullChart.dispatch.NemsisCustomStateElements' },
  { name: 'Times', section: 'dispatch', descriptionKey: 'fullChart.dispatch.LogImportantTimesRelatedToTheCall' },
  { name: 'Timeline', section: 'patient', descriptionKey: 'fullChart.details.VitalSignsAndInterventions' },
  {
    name: 'Patient Information',
    section: 'patient',
    descriptionKey: 'fullChart.patient.AddressAllergiesCurrentMedsAndRelativeInfo',
  },
  { name: 'Billing', section: 'patient', descriptionKey: 'fullChart.patient.InsuranceCMSAndOtherPaymentDetails' },
  { name: 'CC / HPI', section: 'patient', descriptionKey: 'fullChart.patient.ComplaintsHistoryAndOtherDetails' },
  {
    name: 'Initial Assessments',
    section: 'patient',
    descriptionKey: 'fullChart.patient.initialAssessment.InitialAssessmentDetails',
  },
  {
    name: 'Additional Assessments',
    section: 'patient',
    descriptionKey: 'fullChart.patient.additionalAssessments.AdditionalAssessmentsSubHeader',
  },
  { name: 'Fluids', section: 'patient', descriptionKey: 'fullChart.patient.fluids.FluidsSubHeader' },
  { name: 'Narrative', section: 'patient', descriptionKey: 'fullChart.patient.TreatmentAssessmentAndCOCDetails' },
]

const COMMENTS_LABEL = 'Comments'
const PTA_LABEL = 'Mark as PTA'
const SUMMARY_EXCLUDED_TYPES = new Set(['datetime', 'toggle', 'boolean'])

const getElementLabel = (element: IQaChartSummaryElement): string => element.label?.trim() ?? ''

const isValueRecord = (value: unknown): value is IQaChartSummaryValueRecord =>
  typeof value === 'object' &&
  value != null &&
  'recordKey' in value &&
  Array.isArray((value as IQaChartSummaryValueRecord).elements)

export const getValueRecords = (value: unknown): IQaChartSummaryValueRecord[] => {
  if (Array.isArray(value)) return value.filter(isValueRecord)
  if (isValueRecord(value)) return [value]
  return []
}

const collectOptionNames = (value: unknown, includeIds = false): string[] => {
  if (value == null) return []
  if (Array.isArray(value)) return value.flatMap((item) => collectOptionNames(item, includeIds))
  if (typeof value === 'object') {
    if (isValueRecord(value)) return []
    const option = value as { id?: unknown; name?: unknown; options?: unknown }
    if (Array.isArray(option.options)) return collectOptionNames(option.options, includeIds)
    const id = String(option.id ?? '').trim()
    const name = String(option.name ?? '').trim()
    // The "name (id)" form is opt-in (root card only). Everywhere else shows the name alone.
    const display = includeIds && name && id && name !== id ? `${name} (${id})` : name || id
    return display ? [display] : []
  }
  const scalar = String(value).trim()
  return scalar ? [scalar] : []
}

export const formatValue = (value: unknown, includeIds = false): string =>
  collectOptionNames(value, includeIds).join(', ')

const getGroupIdentifier = (item: Record<string, unknown>): string => {
  const idKey = Object.keys(item).find((key) => key !== OPTIONS_KEY && GROUP_ID_KEY_PATTERN.test(key))
  return idKey != null ? String(item[idKey] ?? '').trim() : ''
}

const getGroupInlineName = (item: Record<string, unknown>): string => {
  const nameKey = Object.keys(item).find((key) => GROUP_NAME_KEY_PATTERN.test(key))
  return nameKey != null ? String(item[nameKey] ?? '').trim() : ''
}

export const extractOptionGroups = (value: unknown, includeIds = false): IOptionGroup[] | null => {
  if (!Array.isArray(value)) return null
  const items = value.filter(
    (item): item is Record<string, unknown> =>
      typeof item === 'object' && item != null && Array.isArray((item as Record<string, unknown>).options)
  )
  if (items.length === 0) return null
  return items.map((item) => ({
    id: getGroupIdentifier(item),
    inlineName: getGroupInlineName(item),
    names: collectOptionNames(item.options, includeIds),
  }))
}

export const isContainerElement = (element: IQaChartSummaryElement): boolean =>
  element.fieldType === 'list' || element.fieldType === 'group'

export const isListElement = (element: IQaChartSummaryElement): boolean => element.fieldType === 'list'

export const isPnnvElement = (element: IQaChartSummaryElement): boolean => element.isPnnv === 1

export const formatRecordLabel = (label: string | null | undefined, sequence: number): string => {
  const cleanLabel = label
    ?.trim()
    .replace(/\s+list$/i, '')
    .trim()
  return cleanLabel ? `${cleanLabel} ${sequence}` : String(sequence)
}

// A PNNV field falls back to its companion (matched by pnnvFieldUuid) when it has no own value.
export const resolveElementValue = (
  element: IQaChartSummaryElement,
  siblings: IQaChartSummaryElement[],
  includeIds = false
): string => {
  const own = formatValue(element.value, includeIds)
  if (own || !element.pnnvFieldUuid) return own
  const companion = siblings.find((sibling) => sibling.binding === element.pnnvFieldUuid)
  return companion ? formatValue(companion.value, includeIds) : own
}

export const getPnnvRowLabel = (element: IQaChartSummaryElement, siblings: IQaChartSummaryElement[]): string | null => {
  if (!isPnnvElement(element)) return null
  const twin = siblings.find((sibling) => sibling.pnnvFieldUuid === element.binding)
  const twinValue = twin ? formatValue(twin.value) : ''
  const pnnvValue = formatValue(element.value)
  if (!twinValue || !pnnvValue) return null
  const label = getElementLabel(element)
  const twinLabel = twin ? getElementLabel(twin) : ''
  return label === twinLabel ? `${label} PNNV` : label
}

const elementHasRenderableContent = (element: IQaChartSummaryElement, siblings: IQaChartSummaryElement[]): boolean => {
  if (isContainerElement(element)) {
    return getValueRecords(element.value).some((record) =>
      record.elements.some((child) => elementHasRenderableContent(child, record.elements))
    )
  }
  if (isPnnvElement(element)) return getPnnvRowLabel(element, siblings) != null
  const groups = extractOptionGroups(element.value)
  if (groups && groups.some((group) => group.names.length > 0)) return true
  return Boolean(getElementLabel(element) || resolveElementValue(element, siblings))
}

export const hasRenderableContent = (elements: IQaChartSummaryElement[]): boolean =>
  elements.some((element) => elementHasRenderableContent(element, elements))

interface IChartSummaryLeaf {
  element: IQaChartSummaryElement
  value: string
  category?: string
}

function collectPopulatedLeaves(elements: IQaChartSummaryElement[], category?: string): IChartSummaryLeaf[] {
  return elements.flatMap((element) => {
    if (isPnnvElement(element)) return []
    if (isContainerElement(element)) {
      const childCategory = getElementLabel(element) || category
      return getValueRecords(element.value).flatMap((record) => collectPopulatedLeaves(record.elements, childCategory))
    }
    const value = resolveElementValue(element, elements)
    return value ? [{ element, value, category }] : []
  })
}

const findFieldValue = (record: IQaChartSummaryValueRecord, labels: string[]): string =>
  formatValue(record.elements.find((el) => !isPnnvElement(el) && labels.includes(getElementLabel(el)))?.value)

// Record date / time

export const getRecordDateTime = (
  record: IQaChartSummaryValueRecord
): { time: string; date: string; sortDate: number | null } => {
  const dateElement = record.elements.find((el) => el.fieldType === 'datetime' && !isPnnvElement(el))
  const rawValue = formatValue(dateElement?.value)
  if (!rawValue) return { time: '', date: '', sortDate: null }
  const parsed = new Date(rawValue)
  if (Number.isNaN(parsed.getTime())) return { time: rawValue, date: '', sortDate: null }
  return { time: getFormattedTime(parsed), date: getFormattedDate(parsed), sortDate: parsed.getTime() }
}

type BodySurveyFieldRole = 'present' | 'notDocumented' | 'comment'
type BodySurveyBucket = { present: string[]; notDocumented: string[]; comment: string; inlineName: string }

const getBodySurveyFieldRole = (element: IQaChartSummaryElement): BodySurveyFieldRole | null => {
  const path = element.pcrPath ?? ''
  if (path.includes('examValuesPNNV')) return 'notDocumented'
  if (path.includes('examValues')) return 'present'
  if (path.includes('findings')) return 'comment'
  return null
}

const isBodySurveyElement = (element: IQaChartSummaryElement): boolean =>
  (element.pcrPath ?? '').includes(BODY_SURVEY_PATH_SEGMENT)

export const isBodySurveySection = (section: IQaChartSummarySection): boolean =>
  (section.elements ?? []).some(isBodySurveyElement)

const collectBodySurveyBuckets = (survey: IQaChartSummaryValueRecord): Map<string, BodySurveyBucket> => {
  const buckets = new Map<string, BodySurveyBucket>()

  ;(survey.elements ?? []).forEach((field) => {
    const role = getBodySurveyFieldRole(field)
    if (!role || !Array.isArray(field.value)) return

    field.value.forEach((item) => {
      if (!item || typeof item !== 'object') return
      const entry = item as Record<string, unknown>
      const id = getGroupIdentifier(entry)
      if (!id) return

      let bucket = buckets.get(id)
      if (!bucket) {
        bucket = { present: [], notDocumented: [], comment: '', inlineName: '' }
        buckets.set(id, bucket)
      }

      const inlineName = getGroupInlineName(entry)
      if (inlineName) bucket.inlineName = inlineName

      if (role === 'comment') {
        const comments = entry.comments
        if (typeof comments === 'string' && comments.trim()) bucket.comment = comments.trim()
      } else {
        bucket[role].push(...collectOptionNames(entry.options))
      }
    })
  })

  return buckets
}

const getBodySurveyCategoryName = (locationId: string): string =>
  LOCATION_ID_MAPPING[Number(locationId)] ?? LOCATION_ID_MAPPING.default

const getCategoryRank = (name: string): number => {
  const rank = BODY_SURVEY_CATEGORY_ORDER.indexOf(name)
  return rank === -1 ? BODY_SURVEY_CATEGORY_ORDER.length : rank
}

const groupPartsByCategory = (parts: Array<IBodySurveyPart & { category: string }>): IBodySurveyCategory[] => {
  const partsByCategory = new Map<string, IBodySurveyPart[]>()
  parts.forEach(({ category, ...part }) => {
    const existing = partsByCategory.get(category)
    if (existing) existing.push(part)
    else partsByCategory.set(category, [part])
  })

  return [...partsByCategory.entries()]
    .sort(([left], [right]) => getCategoryRank(left) - getCategoryRank(right))
    .map(([name, categoryParts]) => ({ key: name, name, parts: categoryParts }))
}

const buildBodySurveyGroup = (survey: IQaChartSummaryValueRecord, index: number): IBodySurveyGroup => {
  const parts = [...collectBodySurveyBuckets(survey).entries()]
    .map(([locationId, bucket]) => ({
      key: locationId,
      category: getBodySurveyCategoryName(locationId),
      name: bucket.inlineName || getBodyPartNameById(locationId) || '',
      present: bucket.present.join(', '),
      notDocumented: bucket.notDocumented.join(', '),
      comment: bucket.comment,
    }))
    .filter((part) => part.present || part.notDocumented || part.comment)

  const { time, date } = getRecordDateTime(survey)
  return {
    key: String(survey.recordKey ?? index),
    title: `Body Survey ${index + 1}`,
    time,
    date,
    categories: groupPartsByCategory(parts),
  }
}

export const getBodySurveyGroups = (section: IQaChartSummarySection): IBodySurveyGroup[] | null => {
  const surveyElement = section.elements?.find(isBodySurveyElement)
  if (!surveyElement) return null

  const groups = getValueRecords(surveyElement.value)
    .map(buildBodySurveyGroup)
    .filter((group) => group.categories.length > 0)

  return groups.length ? groups : null
}

// Page metadata & list

export const getPageDescriptionKey = (pageName: string | undefined): string | undefined =>
  pageName ? CHART_SUMMARY_PAGES.find((page) => page.name === pageName.trim())?.descriptionKey : undefined

export const getChartSummaryPageSection = (pageName: string | undefined): ChartSummarySection | undefined =>
  pageName ? CHART_SUMMARY_PAGES.find((page) => page.name === pageName.trim())?.section : undefined

const getChartSummaryPageRank = (pageName: string): number => {
  const index = CHART_SUMMARY_PAGES.findIndex((page) => page.name === pageName.trim())
  return index === -1 ? CHART_SUMMARY_PAGES.length : index
}

export const toChartSummarySlug = (pageName: string): string =>
  pageName
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')

export const buildChartSummaryPageList = (
  chartSummary: IQaChartSummary | null | undefined
): IChartSummaryPageListModel => {
  const allPages = chartSummary?.flatMap((form) => form.pages ?? []) ?? []
  const rootElements =
    allPages.find((page) => page.pageName === ROOT_PAGE_NAME)?.sections?.flatMap((section) => section.elements ?? []) ??
    []
  const pages = allPages
    .filter((page) => page.pageName !== ROOT_PAGE_NAME)
    .map((page, index) => ({ key: `${page.pageId}-${index}`, page }))
    .sort((left, right) => getChartSummaryPageRank(left.page.pageName) - getChartSummaryPageRank(right.page.pageName))

  return { rootElements, pages, isEmpty: allPages.length === 0 }
}

// Event timeline
export const isEventPta = (record: IQaChartSummaryValueRecord): boolean => {
  const pta = findFieldValue(record, [PTA_LABEL]).toLowerCase()
  return pta === '1' || pta === 'true'
}

const getStandardVitalChips = (record: IQaChartSummaryValueRecord): IChartSummaryEventKeyValue[] => {
  const systolic = findFieldValue(record, ['Systolic BP'])
  const diastolic = findFieldValue(record, ['Diastolic BP'])
  return [
    { key: 'HR', value: findFieldValue(record, ['Heart Rate']) },
    { key: 'BP', value: `${systolic}/${diastolic}` },
    { key: 'RR', value: findFieldValue(record, ['Respiratory Rate']) },
    { key: 'SpO2', value: findFieldValue(record, ['SPO2', 'SpO2', 'SpO₂']) },
    { key: 'Temp', value: findFieldValue(record, ['Temperature']), hideOnMobile: true },
  ]
}

export const getEventKeyValues = (
  sectionHeader: string,
  record: IQaChartSummaryValueRecord
): IChartSummaryEventKeyValue[] | null => {
  if (TIMELINE_SUMMARY_CONFIG[sectionHeader]?.renderer !== 'vitals') return null
  return getStandardVitalChips(record)
}

export const getEventNarrative = (record: IQaChartSummaryValueRecord): string =>
  collectPopulatedLeaves(record.elements).find((leaf) => getElementLabel(leaf.element) === COMMENTS_LABEL)?.value ?? ''

export const getEventSummaryText = (
  record: IQaChartSummaryValueRecord,
  keyValues: IChartSummaryEventKeyValue[] | null,
  sectionHeader?: string
): string => {
  if (keyValues) return ''

  const config = sectionHeader ? TIMELINE_SUMMARY_CONFIG[sectionHeader] : undefined
  if (config?.renderer !== 'fields' || !config.fields?.length) return ''

  const { fields, showLabels } = config
  const showTopLevelLabels = showLabels ?? true
  const showGroupedLabels = showLabels ?? false

  const fieldOrder = (element: IQaChartSummaryElement): number => {
    if (isPnnvElement(element) || SUMMARY_EXCLUDED_TYPES.has(element.fieldType ?? '')) return -1
    const label = getElementLabel(element).toLowerCase()
    return fields.findIndex((field) => field.toLowerCase() === label)
  }
  const isSummaryField = (element: IQaChartSummaryElement): boolean => fieldOrder(element) >= 0

  const toFieldText = (
    field: IQaChartSummaryElement,
    peers: IQaChartSummaryElement[],
    showLabel: boolean
  ): string[] => {
    const value = resolveElementValue(field, peers)
    if (!value) return []
    const label = getElementLabel(field)
    return [showLabel && label ? `${label}: ${value}` : value]
  }

  const summarizeGroup = (groupFields: IQaChartSummaryElement[]): string[] =>
    groupFields
      .filter(isSummaryField)
      .sort((a, b) => fieldOrder(a) - fieldOrder(b))
      .flatMap((field) => toFieldText(field, groupFields, showGroupedLabels))

  return record.elements
    .flatMap((element) => {
      if (isPnnvElement(element)) return []
      if (isContainerElement(element)) {
        return getValueRecords(element.value).flatMap((group) => summarizeGroup(group.elements))
      }
      if (!isSummaryField(element)) return []
      return toFieldText(element, record.elements, showTopLevelLabels)
    })
    .join(', ')
}

const dedupeRecords = (records: IQaChartSummaryValueRecord[]): IQaChartSummaryValueRecord[] => {
  const byKey = new Map<string, IQaChartSummaryValueRecord>()

  for (const record of records) {
    const existing = byKey.get(record.recordKey)
    if (!existing) {
      byKey.set(record.recordKey, record)
      continue
    }

    const existingHasDate = getRecordDateTime(existing).sortDate != null
    const recordHasDate = getRecordDateTime(record).sortDate != null

    if (existingHasDate !== recordHasDate) {
      if (recordHasDate) byKey.set(record.recordKey, record)
    } else if (collectPopulatedLeaves(record.elements).length > collectPopulatedLeaves(existing.elements).length) {
      byKey.set(record.recordKey, record)
    }
  }

  return [...byKey.values()]
}

export const getEventEntries = (page: IQaChartSummaryPage): IChartSummaryEventEntry[] =>
  (page.sections ?? [])
    .flatMap((section) => {
      const element = section.elements?.[0]
      if (!element) return []

      const sectionHeader = section.header?.trim() || getElementLabel(element)
      return dedupeRecords(getValueRecords(element.value))
        .filter((record) => collectPopulatedLeaves(record.elements).length > 0)
        .map((record) => ({
          key: `${section.sectionId}-${element.binding}-${record.recordKey}`,
          sectionHeader,
          record,
        }))
    })
    .sort((left, right) => {
      const leftDate = getRecordDateTime(left.record).sortDate ?? 0
      const rightDate = getRecordDateTime(right.record).sortDate ?? 0
      return leftDate - rightDate
    })
